import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Globe, FileText, Wifi, FileCheck, Plus, RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const TYPES = {
  certificate: { label: 'Certificado digital', icon: Shield,    color: 'var(--accent)', fields: ['domain', 'issuer', 'san'] },
  domain:      { label: 'Dominio web',          icon: Globe,     color: 'var(--green)', fields: ['registrar', 'dns'] },
  license:     { label: 'Licencia de software', icon: FileText,  color: '#6B3FA0', fields: ['product', 'seats', 'license_key'] },
  wan_link:    { label: 'Enlace WAN/ISP',        icon: Wifi,      color: '#D45D00', fields: ['bandwidth', 'public_ip', 'circuit_id'] },
  contract:    { label: 'Contrato de soporte',   icon: FileCheck, color: 'var(--ink-2)', fields: ['contract_number', 'amount'] },
};

const STATUS_MAP = {
  active:   { label: 'Activo',       color: 'var(--green)', bg: 'var(--green-dim)', icon: CheckCircle },
  expiring: { label: 'Por vencer',   color: '#D45D00', bg: 'var(--amber-dim)', icon: Clock },
  expired:  { label: 'Vencido',      color: 'var(--red)', bg: 'var(--red-dim)', icon: XCircle },
};

const METADATA_FIELDS = {
  certificate: [
    { key: 'domain',  label: 'Dominio' },
    { key: 'issuer',  label: 'Emisor' },
    { key: 'san',     label: 'SANs' },
  ],
  domain: [
    { key: 'registrar', label: 'Registrar' },
    { key: 'dns',       label: 'Servidores DNS' },
  ],
  license: [
    { key: 'product',     label: 'Producto' },
    { key: 'seats',       label: 'Cantidad de licencias' },
    { key: 'license_key', label: 'Clave de licencia' },
  ],
  wan_link: [
    { key: 'bandwidth',  label: 'Ancho de banda' },
    { key: 'public_ip',  label: 'IP pública' },
    { key: 'circuit_id', label: 'ID de circuito' },
  ],
  contract: [
    { key: 'contract_number', label: 'Número de contrato' },
    { key: 'amount',          label: 'Monto' },
  ],
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.active;
  const Icon = s.icon;
  return (
    <span style={{ color: s.color, fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

function DaysChip({ days }) {
  if (days == null) return <span style={{ color: '#aaa', fontSize: 12 }}>—</span>;
  const d = Math.round(days);
  const color = d < 0 ? 'var(--red)' : d < 30 ? '#D45D00' : 'var(--green)';
  return <span style={{ fontSize: 12, fontWeight: 600, color }}>{d < 0 ? `Vencido hace ${Math.abs(d)}d` : `${d} días`}</span>;
}

export default function LogicalAssets() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ type: 'certificate', name: '', description: '', provider: '', responsible: '', expires_at: '', alert_days: 30, notes: '', metadata: {} });

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterType) params.append('type', filterType);
    if (filterStatus) params.append('status', filterStatus);
    Promise.all([
      fetch(`${API_URL}/logical-assets?${params}`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/logical-assets/summary`, { headers }).then(r => r.json()),
    ]).then(([a, s]) => {
      setAssets(Array.isArray(a) ? a : []);
      setSummary(s);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filterType, filterStatus]);

  const openNew = () => {
    setEditAsset(null);
    setForm({ type: 'certificate', name: '', description: '', provider: '', responsible: '', expires_at: '', alert_days: 30, notes: '', metadata: {} });
    setError('');
    setShowModal(true);
  };

  const openEdit = (a) => {
    setEditAsset(a);
    setForm({
      type: a.type, name: a.name, description: a.description || '',
      provider: a.provider || '', responsible: a.responsible || '',
      expires_at: a.expires_at ? a.expires_at.slice(0, 10) : '',
      alert_days: a.alert_days || 30, notes: a.notes || '',
      metadata: a.metadata || {},
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const url = editAsset ? `${API_URL}/logical-assets/${editAsset.id}` : `${API_URL}/logical-assets`;
      const method = editAsset ? 'PATCH' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify({ ...form, expires_at: form.expires_at || null }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setShowModal(false);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este activo?')) return;
    await fetch(`${API_URL}/logical-assets/${id}`, { method: 'DELETE', headers });
    load();
  };

  const setMeta = (key, value) => setForm(f => ({ ...f, metadata: { ...f.metadata, [key]: value } }));

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Activos lógicos</div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Certificados, dominios, licencias, enlaces y contratos</div>
        </div>
        <button className="btn btn-primary" onClick={openNew} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={15} /> Nuevo activo
        </button>
      </div>

      {/* Summary */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 24 }}>
          {[
            ['Total', summary.total, 'var(--gray-700)'],
            ['Activos', summary.active, 'var(--green)'],
            ['Por vencer', summary.expiring, '#D45D00'],
            ['Vencidos', summary.expired, 'var(--red)'],
            ['Certificados', summary.certificates, 'var(--accent)'],
          ].map(([label, val, color]) => (
            <div key={label} className="card" style={{ padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>{val || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Alertas de vencimiento */}
      {summary?.expired > 0 && (
        <div style={{ background: 'var(--red-dim)', border: '1px solid #F5C0BC', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <XCircle size={16} color="var(--red)" />
          <span style={{ fontSize: 13, color: 'var(--red)', fontWeight: 500 }}>{summary.expired} activo{summary.expired > 1 ? 's' : ''} vencido{summary.expired > 1 ? 's' : ''} — requieren atención inmediata</span>
        </div>
      )}
      {summary?.expiring > 0 && (
        <div style={{ background: 'var(--amber-dim)', border: '1px solid #F5C080', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={16} color="#D45D00" />
          <span style={{ fontSize: 13, color: '#D45D00', fontWeight: 500 }}>{summary.expiring} activo{summary.expiring > 1 ? 's' : ''} por vencer próximamente</span>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
          <select className="form-input" style={{ width: 200 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Todos los tipos</option>
            {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="form-input" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="expiring">Por vencer</option>
            <option value="expired">Vencidos</option>
          </select>
          <button className="btn btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th style={{ width: 160 }}>Tipo</th>
                <th style={{ width: 120 }}>Proveedor</th>
                <th style={{ width: 120 }}>Responsable</th>
                <th style={{ width: 110 }}>Vencimiento</th>
                <th style={{ width: 90 }}>Días</th>
                <th style={{ width: 100 }}>Estado</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : assets.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Sin activos lógicos registrados.</td></tr>
              ) : assets.map(a => {
                const t = TYPES[a.type] || TYPES.certificate;
                const Icon = t.icon;
                return (
                  <tr key={a.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon size={14} color={t.color} style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</span>
                      </div>
                      {a.description && <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{a.description}</div>}
                    </td>
                    <td><span style={{ fontSize: 12, color: t.color }}>{t.label}</span></td>
                    <td style={{ fontSize: 12 }}>{a.provider || '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.responsible || '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.expires_at ? new Date(a.expires_at).toLocaleDateString('es-AR') : '—'}</td>
                    <td><DaysChip days={a.days_until_expiry} /></td>
                    <td><StatusBadge status={a.computed_status} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>Editar</button>
                        {user?.role === 'admin' && <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }} onClick={() => handleDelete(a.id)}>✕</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 560, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 600 }}>{editAsset ? 'Editar activo' : 'Nuevo activo lógico'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {error && <div style={{ background: 'var(--red-dim)', border: '1px solid #F5C0BC', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>{error}</div>}

            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select className="form-input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value, metadata: {} }))} disabled={!!editAsset}>
                  {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Ej: *.tier5.com.ar" />
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <input className="form-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Proveedor</label>
                  <input className="form-input" value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Responsable</label>
                  <input className="form-input" value={form.responsible} onChange={e => setForm(f => ({ ...f, responsible: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Fecha de vencimiento</label>
                  <input className="form-input" type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Alertar X días antes</label>
                  <input className="form-input" type="number" value={form.alert_days} onChange={e => setForm(f => ({ ...f, alert_days: parseInt(e.target.value) }))} min={1} max={365} />
                </div>
              </div>

              {/* Campos específicos por tipo */}
              {METADATA_FIELDS[form.type] && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 8, marginTop: 4 }}>Datos específicos</div>
                  {METADATA_FIELDS[form.type].map(f => (
                    <div key={f.key} className="form-group">
                      <label className="form-label">{f.label}</label>
                      <input className="form-input" value={form.metadata[f.key] || ''} onChange={e => setMeta(f.key, e.target.value)} />
                    </div>
                  ))}
                </>
              )}

              <div className="form-group">
                <label className="form-label">Notas</label>
                <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : editAsset ? 'Guardar cambios' : 'Crear activo'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
